import { html, nothing } from "lit";
import type { FleetWorkUnit } from "../types.ts";
import {
  fleetStatusPillClass,
  fleetUnitStatusLabel,
  fleetUnitStatusTitle,
} from "./fleet-plan-labels.ts";

const GRAPH_UNIT_LIMIT = 12;

export function topoLayers(units: FleetWorkUnit[]): FleetWorkUnit[][] {
  const byId = new Map(units.map((u) => [u.id, u]));
  const remaining = new Map(
    units.map((u) => [u.id, u.dependsOn.filter((d) => byId.has(d)).length]),
  );
  const layers: FleetWorkUnit[][] = [];
  const placed = new Set<string>();
  while (placed.size < units.length) {
    const layer = units.filter((u) => !placed.has(u.id) && (remaining.get(u.id) ?? 0) === 0);
    if (layer.length === 0) {
      layers.push(units.filter((u) => !placed.has(u.id)));
      break;
    }
    layers.push(layer);
    for (const u of layer) {
      placed.add(u.id);
    }
    for (const u of units) {
      if (placed.has(u.id)) {
        continue;
      }
      remaining.set(u.id, u.dependsOn.filter((d) => byId.has(d) && !placed.has(d)).length);
    }
  }
  return layers;
}

function renderNode(unit: FleetWorkUnit, byId: Map<string, FleetWorkUnit>) {
  const deps = unit.dependsOn
    .map((id) => byId.get(id)?.title.slice(0, 24) ?? id.slice(0, 8))
    .join(", ");
  return html`
    <div
      class="work-plan-dag__node ${fleetStatusPillClass(unit.status)}"
      title=${fleetUnitStatusTitle(unit.status)}
      data-unit-id=${unit.id}
      data-unit-status=${unit.status}
    >
      <div class="work-plan-dag__node-title">${unit.title.slice(0, 48)}</div>
      <div class="work-plan-dag__node-meta">
        ${fleetUnitStatusLabel(unit.status)}${deps ? html` · after ${deps}` : nothing}
      </div>
    </div>
  `;
}

function renderCompactStrip(units: FleetWorkUnit[], byId: Map<string, FleetWorkUnit>) {
  const sorted = [...units].sort((a, b) => a.priority - b.priority);
  return html`
    <div class="work-plan-dag work-plan-dag--strip" data-testid="work-plan-dag-strip">
      ${sorted.map((unit) => renderNode(unit, byId))}
    </div>
  `;
}

function renderGraph(units: FleetWorkUnit[], byId: Map<string, FleetWorkUnit>) {
  const layers = topoLayers(units);
  return html`
    <div class="work-plan-dag work-plan-dag--graph" data-testid="work-plan-dag-graph">
      ${layers.map(
        (layer, i) => html`
          ${i > 0 ? html`<div class="work-plan-dag__edge" aria-hidden="true">→</div>` : nothing}
          <div class="work-plan-dag__layer">${layer.map((unit) => renderNode(unit, byId))}</div>
        `,
      )}
    </div>
  `;
}

/** Compact DAG strip, or a left-to-right topological graph for small plans. */
export function renderFleetPlanMiniDag(units: FleetWorkUnit[]) {
  if (units.length === 0) {
    return nothing;
  }
  const byId = new Map(units.map((u) => [u.id, u]));
  if (units.length > GRAPH_UNIT_LIMIT) {
    return html`
      <details class="work-plan-dag-wrap">
        <summary>Unit graph (${units.length} units)</summary>
        ${renderCompactStrip(units, byId)}
      </details>
    `;
  }
  return html`
    <div class="work-plan-dag-wrap">
      <div class="work-plan-dag__caption">Dependency graph</div>
      ${renderGraph(units, byId)}
    </div>
  `;
}
